import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Container, Row, Spinner } from "react-bootstrap";
import { InfoCircle, XCircle } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import ImagePopover from "../../components/tooltips/imagePopover";
import Wrapper from "../../components/wrapper";
import {
  Formik,
  FormikProps,
  Form as FormikForm,
  FieldArray,
  ArrayHelpers,
} from "formik";
import ScrollToFieldError from "../../components/ScrollToFieldError";
import InputColor from "../../components/inputs/inputColor";
import ButtonImg from "./images/button.png";
import FaviconImg from "./images/favicon.png";
import LogotypeImg from "./images/logotype.png";
import BannerImg from "./images/banner.png";
import UploadFile from "../../components/inputs/uploadFile";
import {
  useCreateOrUpdateWhiteLabelProjectMediaMutation,
  useOrganizerWhiteLabelQuery,
} from "../../qraphql/whiteLabel/whiteLabel.hooks";
import ApolloError from "../../components/apolloError";
import { fetchFile } from "../../hooks/useGetEventData";
import { IEventImage } from "./../../types";
import { NetworkStatus } from "@apollo/client";
import {
  WhiteLabelConfigProxyType,
  WhiteLabelProjectMediaTypeInput,
} from "../../../graphql/types";
import { useNavigate } from "react-router-dom";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import { generateId, resetWhiteLabelJR } from "../../helpers";
import { JoyrideHits } from "../../components/joyride/whiteLabel/JoyrideHits";

interface IMediaForm {
  color: string;
  favicon: File | null;
  logotype: File | null;
  banners: IEventImage[] | null;
}

export default function MediaDesign() {
  const { t } = useTranslation();
  const formRef = useRef<FormikProps<IMediaForm>>(null);
  const navigate = useNavigate();
  const urlLocale = useGetUrlLocale();
  const [deletedBenersIds, setDeletedBenersIds] = useState<number[]>([]);

  const {
    data: whiteLabelInfo,
    refetch: refetchWhiteLabelInfo,
    loading: whiteLabelInfoLoading,
    networkStatus,
  } = useOrganizerWhiteLabelQuery({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [
    createOrUpdateWhiteLabelProjectMedia,
    {
      loading: createOrUpdateWhiteLabelProjectMediaLoading,
      error: createOrUpdateWhiteLabelProjectMediaError,
    },
  ] = useCreateOrUpdateWhiteLabelProjectMediaMutation({
    onCompleted() {
      refetchWhiteLabelInfo();
      navigate(`${urlLocale}/project-settings?complete=1`);
      resetWhiteLabelJR();
    },
  });

  const isRefetching = networkStatus === NetworkStatus.refetch;

  useEffect(() => {
    if (!whiteLabelInfo) return;
    if (!whiteLabelInfo.organizer?.whiteLabel?.domain) {
      navigate(`${urlLocale}/custom-domain`);
      return;
    }
    const whiteLabelConfig = whiteLabelInfo.organizer.whiteLabel
      ?.whiteLabelConfig as WhiteLabelConfigProxyType;
    updateMediaFields(whiteLabelConfig).then(
      ({ faviconFile, logotypeFile, bannerFilesObj }) => {
        formRef.current?.setValues({
          favicon: faviconFile,
          logotype: logotypeFile,
          color: whiteLabelConfig?.buttonColor || "#16B054",
          banners:
            bannerFilesObj.length > 0
              ? bannerFilesObj
              : [{ id: generateId("banner"), file: null }],
        });
      }
    );
  }, [whiteLabelInfo]);

  const updateMediaFields = async (
    whiteLabelConfig: WhiteLabelConfigProxyType
  ) => {
    const faviconFile = await fetchFile(
      whiteLabelConfig?.favicon?.link,
      () => {}
    );
    const logotypeFile = await fetchFile(
      whiteLabelConfig?.siteLogo?.link,
      () => {}
    );
    const bannerFilesObj = await Promise.all(
      (whiteLabelConfig?.whiteLabelBannerImages ?? []).map(async (el) => {
        const file = await fetchFile(el.link, () => {});
        return { id: el.id.toString(), file };
      })
    );
    return { faviconFile, logotypeFile, bannerFilesObj };
  };

  const onSubmit = async (values: IMediaForm) => {
    const input: WhiteLabelProjectMediaTypeInput = {
      bannersInput: values.banners
        .filter((el) => el.file !== null && el.id.startsWith("banner"))
        .map((b) => ({
          id: null,
          file: b.file,
        })),
      bannersDeleted: deletedBenersIds,
      buttonColor: values.color,
      favicon: values.favicon,
      siteLogo: values.logotype,
    };
    await createOrUpdateWhiteLabelProjectMedia({
      variables: {
        input,
      },
    });
  };

  const initialValues: IMediaForm = {
    color: "#16B054",
    favicon: null,
    logotype: null,
    banners: [{ id: null, file: null }],
  };

  const handleBanners = (push: ArrayHelpers["push"]) => {
    push({ id: generateId("banner"), file: null });
  };

  if (whiteLabelInfoLoading && !isRefetching) {
    return (
      <Wrapper mainBlockClassName="white-label-wrapper">
        <Container>
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height: 200 }}
          >
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </Container>
      </Wrapper>
    );
  }

  return (
    <Wrapper mainBlockClassName="white-label-wrapper">
      <Container id="media-design">
        <h2 className="fw-bold fs-1 mb-4 mb-md-5">
          {t("whiteLabel.Project Media & Design")}
        </h2>
        <h5 className="fs-5 fw-bold mb-1">
          {t("whiteLabel.Project Main Color")}{" "}
          <ImagePopover img={ButtonImg} className="mb-1 ms-1 fs-5" />
        </h5>
        <div className="text-info mb-3">
          {t(
            "whiteLabel.Primary color used for buttons, links, and key elements across the project to maintain a consistent look."
          )}
        </div>
        <Formik
          innerRef={formRef}
          initialValues={initialValues}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({ isSubmitting, isValid, submitCount, values, setFieldValue }) => {
            return (
              <FormikForm>
                <ScrollToFieldError />
                <Col lg={5} className="mb-5">
                  <InputColor
                    name="color"
                    className="mb-3"
                    placeholder={t("whiteLabel.Main Color")}
                    defaultColor="#16B054"
                    readOnly
                  />
                </Col>

                <h5 className="fs-5 fw-bold mb-1">
                  {t("whiteLabel.Favicon")}{" "}
                  <ImagePopover img={FaviconImg} className="mb-1 ms-1 fs-5" />
                </h5>
                <div className="text-info mb-3">
                  {t(
                    "whiteLabel.Small icon displayed in the browser tab next to your site’s title."
                  )}
                </div>
                <UploadFile
                  name="favicon"
                  accept=".png"
                  placeholder={t("whiteLabel.Upload Favicon Image")}
                  className="mb-3"
                  maxSizeKb={100}
                />
                <Alert variant="light-gray mb-5">
                  <InfoCircle className="ms-1 me-2 mb-1" />
                  <span className="me-1">
                    {t(
                      "whiteLabel.Upload a 32x32 pixel PNG file (max 100 KB) for optimal favicon display."
                    )}
                  </span>
                </Alert>

                <h5 className="fs-5 fw-bold mb-1">
                  {t("whiteLabel.Logotype")}{" "}
                  <ImagePopover img={LogotypeImg} className="mb-1 ms-1 fs-5" />
                </h5>
                <div className="text-info mb-3">
                  {t(
                    "whiteLabel.Project’s main logo, which will appear on the website, emails, and other key places to represent your brand."
                  )}
                </div>
                <UploadFile
                  name="logotype"
                  accept=".png, .svg"
                  placeholder={t("whiteLabel.Upload Logotype")}
                  className="mb-3"
                  maxSizeKb={500}
                />
                <Alert variant="light-gray mb-5">
                  <InfoCircle className="ms-1 me-2 mb-1" />
                  <span className="me-1">
                    {t(
                      "whiteLabel.Upload a PNG or SVG logotype with a size of 300x100 pixels (max 500 KB) for optimal display."
                    )}
                  </span>
                </Alert>

                <h5 className="fs-5 fw-bold mb-1">
                  {t("whiteLabel.Main Banner / Slider")}{" "}
                  <ImagePopover img={BannerImg} className="mb-1 ms-1 fs-5" />
                </h5>
                <div className="text-info mb-3">
                  {t(
                    "whiteLabel.Primary images displayed on the main page to highlight important content."
                  )}
                </div>
                <FieldArray name="banners">
                  {({ push }) => (
                    <>
                      {values.banners.map((banner, index) => (
                        <div className="mt-3 position-relative" key={banner.id}>
                          <UploadFile
                            name={`banners[${index}].file`}
                            accept=".png, .jpg, .jpeg, .webp"
                            placeholder={t("whiteLabel.Upload Banner")}
                            className="w-100"
                            maxSizeKb={5 * 1024}
                            onChange={(file) => {
                              if (!file) {
                                if (!banner.id.startsWith("banner")) {
                                  setDeletedBenersIds((prev) => [
                                    ...prev,
                                    Number(banner.id),
                                  ]);
                                }

                                if (
                                  index === 0 &&
                                  values.banners.length === 1
                                ) {
                                  setFieldValue("banners", [
                                    { id: generateId("banner"), file: null },
                                  ]);
                                } else {
                                  setFieldValue(
                                    "banners",
                                    values.banners.filter((_, i) => i !== index)
                                  );
                                }
                              } else {
                                setFieldValue(`banners[${index}]`, {
                                  id: generateId("banner"),
                                  file,
                                });
                              }
                            }}
                          />
                          {index > 0 && !values.banners[index].file && (
                            <Button
                              variant=""
                              className="text-dark position-absolute top-50 translate-middle-y"
                              style={{ right: 6 }}
                              onClick={() => {
                                const updatedBanners = values.banners.filter(
                                  (_, i) => i !== index
                                );
                                setFieldValue("banners", updatedBanners);
                              }}
                            >
                              <XCircle className="fs-5" />
                            </Button>
                          )}
                        </div>
                      ))}
                      {values.banners.length < 3 && (
                        <Button
                          variant="link"
                          className="text-success px-0 py-0 mt-2"
                          onClick={() => handleBanners(push)}
                        >
                          + {t("whiteLabel.Add More")}
                        </Button>
                      )}
                    </>
                  )}
                </FieldArray>

                <Alert variant="light-gray mt-3 mb-5">
                  <InfoCircle className="ms-1 me-2 mb-1" />
                  <span className="me-1">
                    {t(
                      "whiteLabel.Optimal size is 1920 x 580 pixels (horizontal orientation). Files in JPG, PNG, and WEBP up to 5 MB are supported."
                    )}
                  </span>
                </Alert>

                <Row className="mb-3">
                  <Col sm={4}>
                    <Button
                      variant="success"
                      className="fw-bold w-100 py-2 mb-5"
                      type="submit"
                      disabled={
                        isSubmitting ||
                        (submitCount > 0 && !isValid) ||
                        isRefetching ||
                        createOrUpdateWhiteLabelProjectMediaLoading
                      }
                    >
                      {t("firstStep.save")}
                      {(isSubmitting ||
                        isRefetching ||
                        createOrUpdateWhiteLabelProjectMediaLoading) && (
                        <Spinner
                          animation="border"
                          role="status"
                          size="sm"
                          className="ms-2"
                        />
                      )}
                    </Button>
                  </Col>
                </Row>
                <ApolloError
                  networkError={
                    createOrUpdateWhiteLabelProjectMediaError?.networkError
                  }
                />
              </FormikForm>
            );
          }}
        </Formik>
      </Container>
      <JoyrideHits />
    </Wrapper>
  );
}
